import { useEffect, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import * as Tooltip from '@radix-ui/react-tooltip'
import { useAppStore } from '@/store/appStore'
import { getFinancePageLabel, isStartupChecklistApplicable } from '@/lib/projectClassification'
import { cn } from '@/lib/utils'
import { type ModuleKey } from '@/config/tesseractConfig'
import { isModuleVisible, isPathAllowed, useModuleEntitlement } from '@/config/moduleEntitlements'
import { isFeatureBuilt } from '@/config/featureCatalog'
import { useCanAccessConfig } from '@/hooks/useAuthorization'
import { useFlag } from '@/hooks/useFlag'
import { useIsMobile } from '@/hooks/useIsMobile'
import { useTimesheetStore } from '@/modules/timesheet/stores/timesheetStore'
import { timesheetDataService } from '@/modules/timesheet/services/timesheetDataService'
import { useCurrentAccessSubject } from '@/modules/timesheet/hooks/useCurrentAccessSubject'
import { useAdminPins, resolvePinnedItems } from '@/modules/timesheet/nav/adminPins'
import { useTicketingAdmin } from '@/modules/it-ticketing/lib/useTicketingAdmin'
import { filterWorkerNavByRole, filterWorkerNavByWorkCategory, canApproveTimeRole } from '@/modules/timesheet/nav/roleAccessConfig'
import { APP_VERSION_FULL } from '@/constants/version'
import { LandingIcon } from '@/components/landing/LandingIcon'
import { getLandingHome, listLandingPages } from '@/services/landing/landingService'
import { pagePath } from '@/services/landing/landingTypes'
import {
  LayoutDashboard,
  Home,
  LayoutTemplate,
  Mic,
  Swords,
  Trophy,
  FolderKanban,
  DollarSign,
  Lock,
  Users,
  FileInput,
  Target,
  FlaskConical,
  GitBranch,
  Columns3,
  Radar,
  BarChart3,
  AlertTriangle,
  GanttChart,
  TrendingUp,
  BookOpen,
  Wallet,
  FileText,
  Network,
  ArrowLeftRight,
  ChevronLeft,
  ChevronRight,
  Activity,
  ClipboardList,
  Settings,
  ShieldCheck,
  Shield,
  UserCog,
  ScrollText,
  KeyRound,
  Monitor,
  Building2,
  Cable,
  Package,
  UserPlus,
  Boxes,
  Database,
  Factory,
  Flag,
  Milestone,
  Clock,
  ClipboardCheck,
  Workflow,
  GraduationCap,
  Calculator,
  Sparkles,
  BadgeCheck,
  BookText,
  Wrench,
  Inbox,
  UserCircle2,
  CalendarDays,
  Scan,
  Phone,
  Settings2,
  Rocket,
  MapPin,
  Mail,
  DatabaseZap,
  FileChartColumn,
  Sigma,
  Percent,
  Shuffle,
  FileSpreadsheet,
  History,
  Sliders,
  ListChecks,
  Layers3,
  Gauge,
  Handshake,
  Scale,
  ChartNoAxesCombined,
  ListTree,
  LayoutGrid,
  Map as MapIcon,
  ToggleRight,
  Zap,
  Waypoints,
  MessageSquare,
  ShieldAlert,
  ShoppingBag,
  Coins,
  Send,
  FolderOpen,
  Search,
  Table2,
  ListOrdered,
  Ticket,
} from 'lucide-react'

interface NavItem {
  label: string
  icon: React.ReactNode
  path: string
  key: string
  projectOnly?: boolean
  portfolioOnly?: boolean
  /** Show only when selectedProject.status === 'Startup'. */
  requiresStartupStatus?: boolean
  /**
   * Owning module, for nav entries whose <Route> in App.tsx is wrapped in
   * `isModuleEnabled(...)`. Every other Studio is protected already, because
   * ContextSelector hides the whole tile when the module is off — but the
   * Project Studio borrows pages from non-core modules, so on a deployment
   * that ships `VITE_MODULES` without them the entry rendered, the route did
   * not exist, and the click fell through the catch-all back to project
   * overview with no explanation. Set this and the item hides instead.
   */
  module?: ModuleKey
  /**
   * Catalogued feature (featureCatalog.ts) the page belongs to. A feature not
   * built into this deployment hides its entry, the same way its route shows
   * the "Feature not enabled" notice.
   */
  feature?: string
  /** Hover text when it says more than the short label (shown collapsed and expanded). */
  tooltip?: string
}

// Grouped nav sections - one section per Plan / Execute / Monitor / Report phase.
// Renders as a single section header per group when sidebar is expanded; items
// flow with a thin divider when collapsed. Keeps the flat NavItem shape and
// existing NavSection rendering - just splits the arrays.
type NavGroup = { label?: string; items: NavItem[] }

const portfolioNavGroups: NavGroup[] = [
  {
    items: [
      { label: 'Dashboard', icon: <LayoutDashboard size={16} />, path: '/dashboard', key: 'dashboard', portfolioOnly: true },
    ],
  },
  {
    label: 'Plan',
    items: [
      { label: 'Onboarding', icon: <Rocket size={16} />, path: '/onboarding', key: 'portfolio-onboarding', portfolioOnly: true },
      { label: 'Project Requests', icon: <FileInput size={16} />, path: '/project-requests', key: 'project-requests', portfolioOnly: true },
      { label: 'Scenario Planning', icon: <FlaskConical size={16} />, path: '/scenario-planning', key: 'scenario-planning', portfolioOnly: true },
      { label: 'Priorities & KPIs', icon: <Target size={16} />, path: '/priorities', key: 'priorities', portfolioOnly: true },
      { label: 'ROI', icon: <Sparkles size={16} />, path: '/roi', key: 'roi', portfolioOnly: true },
      { label: 'Analysis & Attributes', icon: <BarChart3 size={16} />, path: '/analysis', key: 'analysis', portfolioOnly: true },
    ],
  },
  {
    label: 'Execute',
    items: [
      { label: 'Active Projects', icon: <FolderKanban size={16} />, path: '/projects', key: 'projects', portfolioOnly: true },
      { label: 'Portfolio Budgeting', icon: <DollarSign size={16} />, path: '/budget-releases', key: 'budget-releases', portfolioOnly: true },
      { label: 'Resource Consumption', icon: <Users size={16} />, path: '/resource-consumption', key: 'resource-consumption', portfolioOnly: true },
      { label: 'Workflow', icon: <GitBranch size={16} />, path: '/workflows', key: 'workflows', portfolioOnly: true },
    ],
  },
  {
    label: 'Monitor',
    items: [
      { label: 'Risks, Issues & Opps', icon: <AlertTriangle size={16} />, path: '/risks', key: 'risks', portfolioOnly: true },
      { label: 'Milestone Analyzer', icon: <Milestone size={16} />, path: '/milestone-analyzer', key: 'milestone-analyzer', portfolioOnly: true },
      { label: 'Program Review', icon: <ClipboardCheck size={16} />, path: '/programs/review', key: 'program-review', portfolioOnly: true },
    ],
  },
  {
    label: 'Reporting',
    items: [
      { label: 'Conversational Analytics', icon: <ChartNoAxesCombined size={16} />, path: '/analytics', key: 'conversational-analytics', portfolioOnly: true },
    ],
  },
]

const projectNavGroups: NavGroup[] = [
  {
    items: [
      { label: 'Project Overview', icon: <ClipboardList size={16} />, path: '/project-overview', key: 'project-overview', projectOnly: true },
    ],
  },
  {
    label: 'Plan',
    items: [
      { label: 'Startup Checklist', icon: <Rocket size={16} />, path: '/startup-checklist', key: 'project-startup-checklist', projectOnly: true, requiresStartupStatus: true },
      { label: 'WBS Hierarchy', icon: <Network size={16} />, path: '/hierarchy', key: 'hierarchy', projectOnly: true },
      { label: 'Implementation Delivery', icon: <Workflow size={16} />, path: '/implementation', key: 'implementation', projectOnly: true },
      { label: 'Scheduling', icon: <GanttChart size={16} />, path: '/gantt', key: 'gantt', projectOnly: true },
      { label: 'Baseline Management', icon: <Wallet size={16} />, path: '/finance', key: 'finance', projectOnly: true },
      { label: 'Estimate Import', tooltip: 'Estimate Import - Load to Twenty5', icon: <FileSpreadsheet size={16} />, path: '/estimate-import', key: 'estimate-import', projectOnly: true },
    ],
  },
  {
    label: 'Execute',
    items: [
      { label: 'Forecast Management', icon: <Activity size={16} />, path: '/forecasts', key: 'forecasts', projectOnly: true },
      { label: 'Resources', icon: <Users size={16} />, path: '/resource-consumption', key: 'project-resources', projectOnly: true },
      { label: 'Supply Chain Management', icon: <Package size={16} />, path: '/materials', key: 'project-materials', projectOnly: true, module: 'supplychain' },
      { label: 'Team Manager', icon: <UserCog size={16} />, path: '/team', key: 'project-team', projectOnly: true },
      { label: 'Additional Attributes', icon: <Settings2 size={16} />, path: '/attributes', key: 'project-attributes', projectOnly: true },
      { label: 'Locations', icon: <MapPin size={16} />, path: '/project-locations', key: 'project-locations', projectOnly: true },
    ],
  },
  {
    label: 'Monitor',
    items: [
      { label: 'Progress & EV', icon: <TrendingUp size={16} />, path: '/evm', key: 'evm', projectOnly: true },
      { label: 'RAID Log', icon: <AlertTriangle size={16} />, path: '/risks', key: 'project-risks', projectOnly: true },
      { label: 'Lessons Learned', icon: <BookOpen size={16} />, path: '/lessons-learned', key: 'lessons-learned', projectOnly: true },
    ],
  },
  {
    label: 'Reporting',
    items: [
      { label: 'Project Reports', icon: <BarChart3 size={16} />, path: '/reports', key: 'reports', projectOnly: true },
      { label: 'Conversational Analytics', icon: <ChartNoAxesCombined size={16} />, path: '/analytics', key: 'conversational-analytics-project', projectOnly: true },
      { label: 'Contracts', icon: <FileText size={16} />, path: '/contracts', key: 'contracts', projectOnly: true, module: 'clm' },
      { label: 'ERP Sync', icon: <Cable size={16} />, path: '/erp-sync', key: 'erp-sync', projectOnly: true },
      { label: 'Currency Rates', icon: <ArrowLeftRight size={16} />, path: '/project-fx', key: 'project-fx', projectOnly: true },
    ],
  },
  {
    items: [
      { label: 'Rates Management', icon: <Calculator size={16} />, path: '/rates', key: 'project-rates', projectOnly: true },
    ],
  },
]

// Agile Delivery nav group (Hybrid Waterfall/Agile feature). Appended into the
// project context only when the agile.enabled capability is on AND the project
// is Agile or Hybrid. The path literals must stay present in source so the
// nav-coverage gates (check:nav + route-context-coverage.test.ts) map each of
// the five agile routes to a nav entry; runtime visibility is what the flag
// gates.
const agileNavGroup: NavGroup = {
  label: 'Agile',
  items: [
    { label: 'Backlog', icon: <ListTree size={16} />, path: '/backlog', key: 'agile-backlog', projectOnly: true },
    { label: 'Board', icon: <LayoutGrid size={16} />, path: '/board', key: 'agile-board', projectOnly: true },
    { label: 'Sprints', icon: <Rocket size={16} />, path: '/sprints', key: 'agile-sprints', projectOnly: true },
    { label: 'Roadmap', icon: <MapIcon size={16} />, path: '/roadmap', key: 'agile-roadmap', projectOnly: true },
    { label: 'Agile Insights', icon: <TrendingUp size={16} />, path: '/agile-insights', key: 'agile-insights', projectOnly: true },
  ],
}

const toolNavItems: NavItem[] = [
  { label: 'Scheduling', icon: <GanttChart size={16} />, path: '/gantt', key: 'gantt', projectOnly: true },
  { label: 'Baseline Management', icon: <Wallet size={16} />, path: '/finance', key: 'finance', projectOnly: true },
  { label: 'Forecast Management', icon: <Activity size={16} />, path: '/forecasts', key: 'forecasts', projectOnly: true },
  { label: 'Progress & EV', icon: <TrendingUp size={16} />, path: '/evm', key: 'evm', projectOnly: true },
  { label: 'Contracts', icon: <FileText size={16} />, path: '/contracts', key: 'contracts' },
  { label: 'Project Reports', icon: <BarChart3 size={16} />, path: '/reports', key: 'reports', projectOnly: true },
  { label: 'Hierarchy', icon: <Network size={16} />, path: '/hierarchy', key: 'hierarchy', projectOnly: true },
]

const contractNavItems: NavItem[] = [
  { label: 'Contract Dashboard', icon: <LayoutDashboard size={16} />, path: '/contracts', key: 'clm-dashboard' },
  { label: 'All Contracts', icon: <FileText size={16} />, path: '/contracts/all', key: 'clm-all' },
  { label: 'My Approvals', icon: <ClipboardList size={16} />, path: '/contracts/approvals', key: 'clm-approvals' },
  { label: 'Clause Templates', icon: <ScrollText size={16} />, path: '/contracts/templates', key: 'clm-templates' },
  { label: 'Preferred Terms', icon: <Shield size={16} />, path: '/contracts/preferred-terms', key: 'clm-terms' },
  { label: 'Customer Analysis', icon: <Building2 size={16} />, path: '/contracts/customers', key: 'clm-customers' },
  { label: 'Workflow Analytics', icon: <BarChart3 size={16} />, path: '/contracts/analytics', key: 'clm-analytics' },
]

const personnelNavItems: NavItem[] = [
  { label: 'Personnel Directory', icon: <Users size={16} />, path: '/personnel', key: 'personnel-directory' },
]

// Workflow Management context: the single home for requesting, approving,
// designing and operating workflows and forms. Grouped by what a person came to
// do. Existing keys are stable; WorkflowManagementHome mirrors these groups.
// The two Operate deep links open a tab of the Studio. Their paths are built
// from the Studio path (not written as one literal) because they are not routes
// of their own: check-sidebar-coverage maps one literal path to one <Route>.
const WM_STUDIO_PATH = '/workflow-management/studio'
const workflowMgmtNavGroups: NavGroup[] = [
  {
    items: [
      { label: 'Overview', icon: <LayoutDashboard size={16} />, path: '/workflow-management/overview', key: 'wm-home' },
    ],
  },
  {
    label: 'My Work',
    items: [
      { label: 'Submit a Request', icon: <Send size={16} />, path: '/workflow-management/requests', key: 'wm-requests' },
      { label: 'My Requests', icon: <Inbox size={16} />, path: '/workflow-management/my-submissions', key: 'wm-my-subs' },
      { label: 'Approvals', icon: <ClipboardCheck size={16} />, path: '/workflow-management/inbox', key: 'wm-inbox' },
      { label: 'Delegations', icon: <UserCog size={16} />, path: '/workflow-management/delegations', key: 'wm-delegations' },
    ],
  },
  {
    label: 'Design',
    items: [
      { label: 'Workflows', icon: <GitBranch size={16} />, path: '/workflow-management/studio', key: 'wm-studio' },
      { label: 'Forms', icon: <FileInput size={16} />, path: '/workflow-management/forms', key: 'wm-forms' },
      { label: 'Templates', icon: <ScrollText size={16} />, path: '/workflow-management/templates', key: 'wm-templates' },
      { label: 'Landing Pages', icon: <LayoutTemplate size={16} />, path: '/workflow-management/pages', key: 'wm-landing-pages' },
      { label: 'Document Libraries', icon: <FolderOpen size={16} />, path: '/workflow-management/libraries', key: 'wm-libraries' },
    ],
  },
  {
    label: 'Operate',
    items: [
      { label: 'Runs', icon: <Activity size={16} />, path: '/workflow-management/runs', key: 'wm-runs' },
      { label: 'Document Control', icon: <ClipboardList size={16} />, path: '/workflow-management/document-control', key: 'wm-document-control' },
      { label: 'Audit', icon: <BookText size={16} />, path: `${WM_STUDIO_PATH}?wf3Tab=audit`, key: 'wm-audit' },
      { label: 'Policies', icon: <Shield size={16} />, path: `${WM_STUDIO_PATH}?wf3Tab=policies`, key: 'wm-policies' },
    ],
  },
  {
    label: 'Reporting',
    items: [
      { label: 'Dashboards', icon: <BarChart3 size={16} />, path: '/workflow-management/dashboards', key: 'wm-dashboards' },
      { label: 'Reports', icon: <Table2 size={16} />, path: '/workflow-management/reports', key: 'wm-reports' },
      { label: 'Request Register', icon: <ListOrdered size={16} />, path: '/workflow-management/reports/request-register', key: 'wm-request-register' },
      { label: 'Search', icon: <Search size={16} />, path: '/workflow-management/search', key: 'wm-search' },
    ],
  },
  {
    label: 'Setup',
    items: [
      { label: 'People Groups', icon: <Users size={16} />, path: '/workflow-management/groups', key: 'wm-groups' },
      { label: 'Select Lists', icon: <ListChecks size={16} />, path: '/workflow-management/select-lists', key: 'wm-select-lists' },
      { label: 'Routing Matrix', icon: <Network size={16} />, path: '/workflow-management/routing-matrix', key: 'wm-routing-matrix' },
      { label: 'Teams', icon: <MessageSquare size={16} />, path: '/workflow-management/teams', key: 'wm-teams' },
      { label: 'AI Connection', icon: <Cable size={16} />, path: '/workflow-management/ai-connection', key: 'wm-ai-connection' },
      { label: 'Access and CUI', icon: <Lock size={16} />, path: '/workflow-management/access', key: 'wm-access' },
      { label: 'Directory (Entra ID)', icon: <Building2 size={16} />, path: '/workflow-management/directory', key: 'wm-directory' },
      { label: 'Email', icon: <Mail size={16} />, path: '/workflow-management/email', key: 'wm-email' },
      { label: 'Sites', icon: <MapPin size={16} />, path: '/workflow-management/sites', key: 'wm-sites' },
      { label: 'Data Import', icon: <DatabaseZap size={16} />, path: '/workflow-management/data-import', key: 'wm-data-import' },
      { label: 'Admin Reports', icon: <FileChartColumn size={16} />, path: '/workflow-management/admin-reports', key: 'wm-admin-reports' },
    ],
  },
  {
    label: 'Governance',
    items: [
      { label: 'Master Data Governance', icon: <Database size={16} />, path: '/workflow-management/mdg', key: 'wm-mdg' },
      { label: 'Dynamic SAP Actions', icon: <Zap size={16} />, path: '/workflow-management/dynamic-actions', key: 'wm-dynamic-actions' },
    ],
  },
]

// Training (switchable per organization with the training.enabled flag, off by
// default). The route literals stay in source so the coverage gates map them;
// the Sidebar component only gates runtime visibility.
const trainingNavGroup: NavGroup = {
  label: 'Training',
  items: [
    { label: 'My Training', icon: <GraduationCap size={16} />, path: '/workflow-management/training', key: 'wm-training' },
    { label: 'Training Compliance', icon: <BarChart3 size={16} />, path: '/workflow-management/training/compliance', key: 'wm-training-compliance' },
  ],
}
const trainingSetupItem: NavItem = { label: 'Training', icon: <GraduationCap size={16} />, path: '/workflow-management/training/setup', key: 'wm-training-setup' }

// Cost Management context - the government-contractor cost accounting workbench
// on the S/4HANA universal ledger. Ordered the way an accountant works it:
// see the ledger, define the pool/segment structure, run allocations and
// burdens, move cost between objects, then report incurred cost.
const costNavItems: NavItem[] = [
  { label: 'Cost Overview', icon: <LayoutDashboard size={16} />, path: '/cost/overview', key: 'cost-home' },
  { label: 'Universal Ledger', icon: <Database size={16} />, path: '/cost/ledger', key: 'cost-ledger' },
  { label: 'Pools & Rate Segments', icon: <Sigma size={16} />, path: '/cost/rates', key: 'cost-rates' },
  { label: 'Direct Allocations', icon: <Shuffle size={16} />, path: '/cost/allocations', key: 'cost-allocations' },
  { label: 'Burden Rates', icon: <Percent size={16} />, path: '/cost/burdens', key: 'cost-burdens' },
  { label: 'Cost Transfers', icon: <ArrowLeftRight size={16} />, path: '/cost/transfers', key: 'cost-transfers' },
  { label: 'Incurred Cost Reporting', icon: <FileSpreadsheet size={16} />, path: '/cost/icr', key: 'cost-icr' },
  { label: 'Pegged Cost Accrual', icon: <Coins size={16} />, path: '/cost/pegged-accrual', key: 'cost-pegged-accrual', feature: 'cost-management.pegged-accrual' },
  { label: 'Posting Log', icon: <History size={16} />, path: '/cost/postings', key: 'cost-postings' },
]

// CRM Studio context - the revenue front end. Ordered the way a seller works
// it: the home roll-up, then the accounts that everything else hangs off, then
// the interaction log, then the studio's own settings. Leads, pipeline,
// Customer 360, products, quotes, and the portal join this list as their
// phases land; a route is only listed here once its page exists.
const crmNavItems: NavItem[] = [
  { label: 'CRM Home', icon: <LayoutDashboard size={16} />, path: '/crm', key: 'crm-home' },
  { label: 'Quick Capture', icon: <Mic size={16} />, path: '/crm/capture', key: 'crm-capture' },
  { label: 'Leads', icon: <UserPlus size={16} />, path: '/crm/leads', key: 'crm-leads' },
  { label: 'Pipeline', icon: <Columns3 size={16} />, path: '/crm/pipeline', key: 'crm-pipeline' },
  { label: 'Accounts', icon: <Building2 size={16} />, path: '/crm/accounts', key: 'crm-accounts' },
  { label: 'Interactions', icon: <MessageSquare size={16} />, path: '/crm/interactions', key: 'crm-interactions' },
  { label: 'Comms Inbox', icon: <Inbox size={16} />, path: '/crm/comms', key: 'crm-comms' },
  { label: 'Service & Escalations', icon: <ShieldAlert size={16} />, path: '/crm/service', key: 'crm-service' },
  { label: 'Competitors', icon: <Swords size={16} />, path: '/crm/competitors', key: 'crm-competitors' },
  { label: 'Forecast', icon: <TrendingUp size={16} />, path: '/crm/forecast', key: 'crm-forecast' },
  { label: 'Sales Strategy', icon: <Target size={16} />, path: '/crm/strategy', key: 'crm-strategy' },
  { label: 'Team Performance', icon: <Trophy size={16} />, path: '/crm/performance', key: 'crm-performance' },
  { label: 'Quotes', icon: <FileText size={16} />, path: '/crm/quotes', key: 'crm-quotes' },
  { label: 'Customer Portal', icon: <ShoppingBag size={16} />, path: '/crm/portal', key: 'crm-portal' },
  { label: 'Products', icon: <Package size={16} />, path: '/crm/products', key: 'crm-products' },
  { label: 'Price Lists', icon: <Percent size={16} />, path: '/crm/price-lists', key: 'crm-price-lists' },
  { label: 'Pricing Rules', icon: <ShieldAlert size={16} />, path: '/crm/price-rules', key: 'crm-price-rules' },
  { label: 'Product Content', icon: <Layers3 size={16} />, path: '/crm/pim', key: 'crm-pim' },
  { label: 'Pipelines', icon: <GitBranch size={16} />, path: '/crm/pipelines', key: 'crm-pipelines' },
  { label: 'CRM Settings', icon: <Settings size={16} />, path: '/crm/settings', key: 'crm-settings' },
]

// IT Ticketing Studio context, ported from the standalone support-ticketing
// app. Every settings page lives as a tab on Admin, which is shown only to
// IT Ticketing admins (00589; see itTicketingAdminNavKey below).
const itTicketingNavItems: NavItem[] = [
  { label: 'Tickets', icon: <Ticket size={16} />, path: '/it-ticketing', key: 'it-ticketing-home' },
  { label: 'Reports', icon: <BarChart3 size={16} />, path: '/it-ticketing/reports', key: 'it-ticketing-reports' },
  { label: 'History', icon: <History size={16} />, path: '/it-ticketing/history', key: 'it-ticketing-history' },
  { label: 'Admin', icon: <ShieldCheck size={16} />, path: '/it-ticketing/admin', key: 'it-ticketing-admin' },
]
const itTicketingAdminNavKey = 'it-ticketing-admin'

// Analytics Studio - the conversational analytics workspace. A single
// cross-cutting surface today (the engine spans all SAP + Tesseract data);
// the array leaves room for saved-analysis or template views later.
const analyticsNavItems: NavItem[] = [
  { label: 'Conversational Analytics', icon: <ChartNoAxesCombined size={16} />, path: '/analytics', key: 'analytics-workspace' },
  { label: 'Knowledge Graph', icon: <Waypoints size={16} />, path: '/analytics/knowledge-graph', key: 'analytics-knowledge-graph' },
]

// Organizations - the over-arching layer (slice 2). Its own section, distinct from
// the management contexts. Hierarchy is the org-native home; Org Directory + People
// reuse the existing chart + roster surfaces. The CAS / DCAA indirect rate model
// moved to the Cost Management Studio (/cost/rates), where it sits next to the
// ledger, allocations, and incurred cost reporting that feed it.
const organizationalNavItems: NavItem[] = [
  { label: 'Org Hierarchy', icon: <Network size={16} />, path: '/organizations', key: 'org-hierarchy' },
  { label: 'Org Directory', icon: <Building2 size={16} />, path: '/config/org-directory', key: 'org-directory' },
  { label: 'People', icon: <Users size={16} />, path: '/personnel', key: 'org-people' },
  { label: 'Resource Demands', icon: <UserPlus size={16} />, path: '/organizations/resource-demands', key: 'org-resource-demands' },
  { label: 'Rates Management', icon: <Sigma size={16} />, path: '/cost/rates', key: 'org-rates' },
]

// Ordered as a planner works: home, then demand → supply → inventory → capacity → GPD →
// promising → response → control tower → supplier → compliance → sustainment, and finally
// the S&OP / IBP cycle that reconciles all of them to P&L, EV, cash, and funded scope.
// The Actual Cost Pegging Engine pages sit in their own group below it.
const supplyChainNavItems: NavItem[] = [
  { label: 'Planning Home', icon: <LayoutDashboard size={16} />, path: '/supply-chain', key: 'sc-dashboard' },
  { label: 'Demand Workbench', icon: <TrendingUp size={16} />, path: '/supply-chain/demand', key: 'sc-demand' },
  { label: 'Supply & MRP', icon: <Factory size={16} />, path: '/supply-chain/supply', key: 'sc-supply' },
  { label: 'Inventory & DDMRP', icon: <Boxes size={16} />, path: '/supply-chain/inventory', key: 'sc-inventory' },
  { label: 'Capacity', icon: <Gauge size={16} />, path: '/supply-chain/capacity', key: 'sc-capacity' },
  { label: 'GPD & Pegging', icon: <Network size={16} />, path: '/supply-chain/gpd', key: 'sc-gpd' },
  { label: 'Order Promising', icon: <Handshake size={16} />, path: '/supply-chain/promising', key: 'sc-promising' },
  { label: 'Scenarios & Response', icon: <GitBranch size={16} />, path: '/supply-chain/response', key: 'sc-response' },
  { label: 'Control Tower', icon: <Radar size={16} />, path: '/supply-chain/control-tower', key: 'sc-control-tower' },
  { label: 'Supplier & SCFM', icon: <ClipboardList size={16} />, path: '/supply-chain/supplier', key: 'sc-supplier' },
  { label: 'Compliance', icon: <Shield size={16} />, path: '/supply-chain/compliance', key: 'sc-compliance' },
  { label: 'Procurement Compliance', icon: <ClipboardCheck size={16} />, path: '/supply-chain/procurement-compliance', key: 'sc-proc-compliance' },
  { label: 'Sustainment & MRO', icon: <Wrench size={16} />, path: '/supply-chain/sustainment', key: 'sc-sustainment' },
  { label: 'S&OP / IBP', icon: <Scale size={16} />, path: '/supply-chain/sop', key: 'sc-sop' },
]

// The Actual Cost Pegging Engine: every page of that functionality, one group
// in the Supply Chain sidebar, in this order. A new pegging page is one line
// here (plus its route). Never label it with the acronym. Pegged Cost Accrual
// stays in the Cost Management sidebar.
const PEG_ENGINE_GROUP_LABEL = 'Actual Cost Pegging Engine'
const pegEngineNavItems: NavItem[] = [
  { label: 'Actual Cost Pegging', icon: <Coins size={16} />, path: '/supply-chain/cost-pegging', key: 'sc-cost-pegging', feature: 'supplychain.cost-pegging' },
  { label: 'Part Genealogy', icon: <Waypoints size={16} />, path: '/supply-chain/genealogy', key: 'sc-genealogy', feature: 'supplychain.genealogy' },
  { label: 'Pegging Exceptions', icon: <ShieldAlert size={16} />, path: '/supply-chain/pegging-exceptions', key: 'sc-pegging-exceptions', feature: 'supplychain.pegging-exceptions' },
]

const supplyChainNavGroups: NavGroup[] = [
  { items: supplyChainNavItems },
  { label: PEG_ENGINE_GROUP_LABEL, items: pegEngineNavItems },
]

// Timesheet nav has three pieces:
//
//   1. `timesheetDashboardItem` - single Dashboard link pinned to the top.
//   2. `timesheetHourlyItems` / `timesheetSalaryItems` - worker-facing
//      surfaces specific to each pay type. Only one set is shown at a
//      time (controlled by the [ Salary | Hourly ] pill at the top).
//   3. A single "Admin & Reporting" button pinned at the bottom of the
//      sidebar that navigates to `/timesheet/admin` - a hub page listing
//      every cross-cutting admin/compliance/audit surface, grouped by
//      purpose. Keeps the daily worker view clean.
const timesheetDashboardItem: NavItem = {
  label: 'Dashboard', icon: <LayoutDashboard size={16} />, path: '/timesheet', key: 'ts-dashboard',
}

const timesheetHourlyItems: NavItem[] = [
  { label: 'Clock In / Clock Out', icon: <Scan size={16} />, path: '/timesheet/wand', key: 'ts-wand' },
  { label: 'My Manufacturing Day', icon: <Wrench size={16} />, path: '/timesheet/my-manufacturing-day', key: 'ts-mfg-day' },
  { label: 'My Manufacturing Week', icon: <CalendarDays size={16} />, path: '/timesheet/my-week', key: 'ts-mfg-week' },
  { label: 'Floor Activity', icon: <Activity size={16} />, path: '/timesheet/floor-activity', key: 'ts-floor-activity' },
  { label: 'Exceptions Queue', icon: <AlertTriangle size={16} />, path: '/timesheet/exceptions', key: 'ts-exceptions' },
  { label: 'Call-Out Roster', icon: <Phone size={16} />, path: '/timesheet/call-out-roster', key: 'ts-call-out-roster' },
  { label: 'My PTO Calendar', icon: <CalendarDays size={16} />, path: '/timesheet/my-pto-calendar', key: 'ts-my-pto-calendar-hourly' },
]

// Stream F - subcontractor self-entry nav. ICs see their timesheet; leads
// additionally get the Sub Approvals queue. No Salary/Hourly pill (subs
// charge against the prime's codes, not a pay-type grid).
const timesheetSubcontractorItems: NavItem[] = [
  { label: 'My Timesheet', icon: <Clock size={16} />, path: '/timesheet/my', key: 'ts-sub-my' },
]
const timesheetSubLeadItems: NavItem[] = [
  { label: 'My Timesheet', icon: <Clock size={16} />, path: '/timesheet/my', key: 'ts-sub-my' },
  { label: 'Sub Approvals', icon: <Building2 size={16} />, path: '/timesheet/sub-approvals', key: 'ts-sub-approvals-nav' },
]

const timesheetSalaryItems: NavItem[] = [
  { label: 'My Timesheet', icon: <Clock size={16} />, path: '/timesheet/my', key: 'ts-my' },
  // Hidden from the sidebar for now (route + page remain; restore this line to re-list).
  // { label: 'My Utilization', icon: <TrendingUp size={16} />, path: '/timesheet/my-utilization', key: 'ts-my-utilization' },
  { label: 'My PTO Calendar', icon: <CalendarDays size={16} />, path: '/timesheet/my-pto-calendar', key: 'ts-my-pto-calendar-salary' },
]

// Approval Queue - a first-class worker-nav surface for users who can approve
// time (supervisors / leads / payroll). Injected right after My Timesheet by
// buildWorkerNavItems, gated on the approver check (see isTimeApprover).
const timesheetApprovalsItem: NavItem = {
  label: 'Approval Queue', icon: <ClipboardCheck size={16} />, path: '/timesheet/approvals', key: 'ts-approvals-nav',
}

// Office hourly workers don't clock in at a kiosk - they enter time on the grid.
const officeHourlyGridItem: NavItem = { label: 'My Timesheet', icon: <Clock size={16} />, path: '/timesheet/my', key: 'ts-my-office-hourly' }
// Manufacturing self-service views (a worker's own reconciled day/week). Present
// in the hourly list already; injected for salaried manufacturing workers.
const manufacturingSelfItems: NavItem[] = [
  { label: 'My Manufacturing Day', icon: <Wrench size={16} />, path: '/timesheet/my-manufacturing-day', key: 'ts-mfg-day' },
  { label: 'My Manufacturing Week', icon: <CalendarDays size={16} />, path: '/timesheet/my-week', key: 'ts-mfg-week' },
]

// Team / manager surfaces - split into their own "Team" section so the personal
// "My Time" items read cleanly. Everything else is the worker's own stuff.
const TEAM_NAV_PATHS = new Set<string>([
  '/timesheet/approvals',
  '/timesheet/floor-activity',
  '/timesheet/exceptions',
  '/timesheet/call-out-roster',
])

/**
 * Build the worker-facing nav for the active pay type + work category + role,
 * grouped into labeled sections ("My Time" = personal, "Team" = manager surfaces)
 * so the sidebar is scannable:
 *   - role gate drops manager surfaces for individual contributors,
 *   - work-category gate drops the kiosk/manufacturing self-service items for
 *     OFFICE workers (and gives office hourly the grid instead),
 *   - manufacturing workers get the day/week floor views (injected for salary).
 */
function buildWorkerNavItems(
  payType: 'hourly' | 'salary',
  workCategory: string | null | undefined,
  role: string | null | undefined,
  isApprover: boolean,
): NavGroup[] {
  const base = payType === 'hourly' ? timesheetHourlyItems : timesheetSalaryItems
  let items = filterWorkerNavByWorkCategory(filterWorkerNavByRole(base, role), workCategory)
  const hasPath = (p: string) => items.some((i) => i.path === p)
  if (workCategory === 'manufacturing') {
    for (const m of manufacturingSelfItems) if (!hasPath(m.path)) items = [...items, m]
  } else if (payType === 'hourly' && !hasPath('/timesheet/my')) {
    items = [officeHourlyGridItem, ...items]
  }
  if (isApprover && !hasPath(timesheetApprovalsItem.path)) {
    items = [...items, timesheetApprovalsItem]
  }
  // Partition into personal ("My Time") vs manager ("Team") sections; drop empty.
  const mine = items.filter((i) => !TEAM_NAV_PATHS.has(i.path))
  const team = items.filter((i) => TEAM_NAV_PATHS.has(i.path))
  const groups: NavGroup[] = []
  if (mine.length) groups.push({ label: 'My Time', items: mine })
  if (team.length) groups.push({ label: 'Team', items: team })
  return groups
}

const myWorkNavItems: NavItem[] = [
  { label: 'Home', icon: <Sparkles size={16} />, path: '/my-work', key: 'mw-home' },
  { label: 'Action Inbox', icon: <Inbox size={16} />, path: '/my-work/inbox', key: 'mw-inbox' },
  { label: 'My Tasks', icon: <ClipboardList size={16} />, path: '/my-work/tasks', key: 'mw-tasks' },
  { label: 'My Approvals', icon: <ClipboardCheck size={16} />, path: '/contracts/approvals', key: 'mw-approvals' },
  { label: 'This Week', icon: <Clock size={16} />, path: '/timesheet/my', key: 'mw-timesheet' },
  { label: 'My Risks', icon: <AlertTriangle size={16} />, path: '/my-work/risks', key: 'mw-risks' },
  { label: 'Calendar', icon: <CalendarDays size={16} />, path: '/my-work/calendar', key: 'mw-calendar' },
  { label: 'Profile', icon: <UserCircle2 size={16} />, path: '/my-profile', key: 'mw-profile' },
  { label: 'Skill Approvals', icon: <BadgeCheck size={16} />, path: '/skill-approvals', key: 'mw-skill-approvals' },
]

const configNavGroups: NavGroup[] = [
  {
    label: 'Overview',
    items: [
      { label: 'Dashboard', icon: <LayoutDashboard size={16} />, path: '/config', key: 'config-dashboard' },
    ],
  },
  {
    label: 'Identity & Access',
    items: [
      { label: 'User Management', icon: <UserCog size={16} />, path: '/config/users', key: 'config-users' },
      { label: 'Access & Invites', icon: <UserPlus size={16} />, path: '/config/access', key: 'config-access' },
      { label: 'Org Directory', icon: <GitBranch size={16} />, path: '/config/org-directory', key: 'config-org-directory' },
      { label: 'Role Assignments', icon: <KeyRound size={16} />, path: '/config/roles', key: 'config-roles' },
      { label: 'ABAC Policies', icon: <Shield size={16} />, path: '/config/policies', key: 'config-policies' },
      { label: 'UI Policy Mappings', icon: <Monitor size={16} />, path: '/config/ui-mappings', key: 'config-ui-mappings' },
      { label: 'Project Roles', icon: <UserCog size={16} />, path: '/config/project-roles', key: 'config-project-roles' },
    ],
  },
  // Workforce skill/work catalogs only - financial + structural reference and
  // system tools each get their own group below (the old 11-item Catalogs
  // grab-bag was the navbar-cleanup ask, 2026-06-11).
  {
    label: 'Catalogs',
    items: [
      { label: 'Disciplines', icon: <Layers3 size={16} />, path: '/config/disciplines', key: 'config-disciplines' },
      { label: 'Expertise Catalog', icon: <GraduationCap size={16} />, path: '/config/expertise', key: 'config-expertise' },
      { label: 'Certifications', icon: <BadgeCheck size={16} />, path: '/config/certifications', key: 'config-certifications' },
      { label: 'Tools / Technologies', icon: <Wrench size={16} />, path: '/config/tools', key: 'config-tools' },
    ],
  },
  {
    label: 'Financial Setup',
    items: [
      { label: 'Chart of Accounts', icon: <BookText size={16} />, path: '/config/chart-of-accounts', key: 'config-chart-of-accounts' },
      { label: 'Cost Planning Accounts', icon: <Calculator size={16} />, path: '/config/cost-planning-accounts', key: 'config-cost-planning-accounts' },
      { label: 'Budget Categories', icon: <DollarSign size={16} />, path: '/config/budget-categories', key: 'config-budget-categories' },
    ],
  },
  {
    label: 'Project Setup',
    items: [
      { label: 'WBS Attribute Library', icon: <Sigma size={16} />, path: '/config/wbs-attribute-library', key: 'config-wbs-attribute-library' },
      { label: 'Resource Planning', icon: <Sliders size={16} />, path: '/config/resource-planning', key: 'config-resource-planning' },
      { label: 'Resource Fields', icon: <ListChecks size={16} />, path: '/config/resource-fields', key: 'config-resource-fields' },
      { label: 'Project Lifecycle', icon: <Workflow size={16} />, path: '/config/project-lifecycle', key: 'config-project-lifecycle' },
      { label: 'Workflow Builder', icon: <GitBranch size={16} />, path: '/workflows', key: 'config-workflows' },
      { label: 'Locations', icon: <MapPin size={16} />, path: '/config/locations', key: 'config-locations' },
    ],
  },
  {
    label: 'Operations',
    items: [
      { label: 'Standard Operating Procedures', icon: <ClipboardList size={16} />, path: '/config/sops', key: 'config-sops' },
      { label: 'Manufacturing Time', icon: <Clock size={16} />, path: '/config/manufacturing-time', key: 'config-manufacturing-time' },
      { label: 'Time Evaluation Rules', icon: <Calculator size={16} />, path: '/config/time-evaluation-rules', key: 'config-time-evaluation-rules' },
    ],
  },
  {
    label: 'System',
    items: [
      { label: 'Instance Setup', icon: <ClipboardCheck size={16} />, path: '/config/instance-setup', key: 'config-instance-setup' },
      { label: 'Client Setup', icon: <Layers3 size={16} />, path: '/config/client-setup', key: 'config-client-setup' },
      { label: 'SAP Integration', icon: <Cable size={16} />, path: '/sap-integration', key: 'config-sap-integration' },
      { label: 'Demo Library', icon: <Boxes size={16} />, path: '/config/demo-library', key: 'config-demo-library' },
      { label: 'Capabilities', icon: <ToggleRight size={16} />, path: '/config/capabilities', key: 'config-capabilities' },
      { label: 'Feature Flags', icon: <Flag size={16} />, path: '/config/feature-flags', key: 'config-feature-flags' },
      { label: 'Audit Log', icon: <ScrollText size={16} />, path: '/config/audit', key: 'config-audit' },
    ],
  },
]

function SidebarTooltip({ label, enabled, children }: { label: string; enabled: boolean; children: React.ReactNode }) {
  if (!enabled) return <>{children}</>
  return (
    <Tooltip.Root delayDuration={200}>
      <Tooltip.Trigger asChild>{children}</Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content
          side="right"
          sideOffset={8}
          className="rounded-md bg-slate-900 px-2.5 py-1.5 text-xs text-white shadow-lg border border-white/10 animate-fade-in z-50"
        >
          {label}
          <Tooltip.Arrow className="fill-slate-900" />
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  )
}

function NavSection({ items, label, expanded, isActive, onNav }: {
  items: NavItem[]
  label?: string
  expanded: boolean
  isActive: (path: string) => boolean
  onNav: (item: NavItem) => void
}) {
  if (items.length === 0) return null

  return (
    <div>
      {label && expanded && (
        <div className="px-4 pt-1.5 pb-0.5 text-[9px] uppercase tracking-wider text-white/35 font-medium">
          {label}
        </div>
      )}
      {!label && expanded && <div className="h-1" />}
      <ul className="space-y-0.5 px-2">
        {items.map((item) => (
          <li key={item.key}>
            <SidebarTooltip label={item.tooltip ?? item.label} enabled={!expanded || !!item.tooltip}>
              <button
                onClick={() => onNav(item)}
                className={cn(
                  'w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-[12px]',
                  'transition-colors duration-150',
                  isActive(item.path)
                    ? 'bg-brand-500 text-white'
                    : 'text-white/70 hover:bg-white/10 hover:text-white'
                )}
                aria-current={isActive(item.path) ? 'page' : undefined}
                aria-label={!expanded ? item.label : undefined}
              >
                <span className="flex-shrink-0">{item.icon}</span>
                {expanded && <span className="truncate">{item.label}</span>}
              </button>
            </SidebarTooltip>
          </li>
        ))}
      </ul>
    </div>
  )
}

export function Sidebar() {
  const location = useLocation()
  const navigate = useNavigate()
  const storeExpanded = useAppStore((s) => s.sidebarExpanded)
  const toggleSidebar = useAppStore((s) => s.toggleSidebar)
  const setSidebarExpanded = useAppStore((s) => s.setSidebarExpanded)
  const mobileNavOpen = useAppStore((s) => s.mobileNavOpen)
  const setMobileNavOpen = useAppStore((s) => s.setMobileNavOpen)
  // On mobile the sidebar is a full-width slide-in drawer that always shows
  // labels; the collapsed-rail width only applies on desktop.
  const isMobile = useIsMobile()
  const expanded = isMobile ? true : storeExpanded
  const portfolio = useAppStore((s) => s.selectedPortfolio)
  const selectedProject = useAppStore((s) => s.selectedProject)
  const managementContext = useAppStore((s) => s.managementContext)
  const currentUser = useAppStore((s) => s.currentUser)
  // Per-user module entitlement (re-renders the nav when it loads).
  const entitlement = useModuleEntitlement()
  const autoRouteTimesheetPayType = useAppStore((s) => s.autoRouteTimesheetPayType)
  const tsEmployeeId = useTimesheetStore((s) => s.currentEmployeeId)
  // Subscribe so the sidebar re-renders and re-resolves the persona's pay lane
  // once the Supabase roster replaces the boot seed after a refresh (otherwise
  // an unresolved persona falls back to the salary default - wrong for hourly).
  useAppStore((s) => s.referenceDataVersion)

  const isProjectContext = managementContext === 'project'
  const isContractContext = managementContext === 'contract'
  const isSupplyChainContext = managementContext === 'supplychain'
  const isConfigContext = managementContext === 'configuration'
  const isTimesheetContext = managementContext === 'timesheet'
  const isMyWorkContext = managementContext === 'mywork'
  const isPersonnelContext = managementContext === 'personnel'
  const isOrganizationalContext = managementContext === 'organizational'
  const isWorkflowMgmtContext = managementContext === 'workflow-management'
  const isCostContext = managementContext === 'cost'
  const isCrmContext = managementContext === 'crm'
  const isItTicketingContext = managementContext === 'it-ticketing'
  const { isAdmin: isTicketingAdmin } = useTicketingAdmin(isItTicketingContext)
  const isAnalyticsContext = managementContext === 'analytics'
  const canAccessConfig = useCanAccessConfig()

  // Mobile: default the nav to the collapsed rail so it doesn't eat the narrow
  // viewport (an expanded sidebar crushes content at phone widths). Collapses on
  // first mount if on a phone, and whenever the viewport crosses into phone width.
  // The effect only re-runs when `isMobile` flips, so a manual expand on mobile sticks.
  useEffect(() => {
    if (isMobile) setSidebarExpanded(false)
  }, [isMobile, setSidebarExpanded])
  // Stream F - subcontractor personas get their own nav (self-entry +,
  // for leads, the approvals queue) instead of the Salary/Hourly pill.
  const tsEmployee = isTimesheetContext ? timesheetDataService.getEmployee(tsEmployeeId) : undefined
  const isSubcontractor = tsEmployee?.payClass === 'subcontractor'
  // Role-access split (#1 P2): hide manager-only worker surfaces (Floor
  // Activity / Exceptions / Call-Out) from individual contributors. Fail-open
  // for managers / unknown roles.
  const { subject: accessSubject } = useCurrentAccessSubject()

  // "My Shortcuts" - the admin/reporting surfaces this persona pinned in the hub,
  // shared via the adminPins store so the sidebar mirrors the hub's ★ set live.
  const { pinned: pinnedAdminKeys } = useAdminPins(accessSubject?.id ?? 'default', accessSubject?.systemRole)
  const shortcutNavItems: NavItem[] = useMemo(
    () =>
      resolvePinnedItems(pinnedAdminKeys, accessSubject?.systemRole).map((it) => ({
        label: it.label,
        icon: it.icon,
        path: it.path,
        key: it.key,
      })),
    [pinnedAdminKeys, accessSubject?.systemRole],
  )
  // Paths already shown under "My Shortcuts" - so the structured worker nav below
  // can drop them and nothing is listed twice (e.g. a pinned Approval Queue).
  const pinnedShortcutPaths = useMemo(() => new Set(shortcutNavItems.map((i) => i.path)), [shortcutNavItems])

  // #5 - route the worker to their lane (hourly clock-in/out vs salary grid)
  // from their payClass, so they land on the right section without flipping the
  // pill. A manual flip this session wins (autoRoute no-ops once manual).
  useEffect(() => {
    const pc = tsEmployee?.payClass
    if (pc === 'hourly' || pc === 'salary') autoRouteTimesheetPayType(pc)
  }, [tsEmployee?.payClass, autoRouteTimesheetPayType])
  const isSubLeadPersona = tsEmployee?.isSubLead === true
  // Pay lane (salary vs hourly) is AUTO-DETERMINED from the logged-in user's
  // employee record - their payClass when set, otherwise derived from the
  // exempt flag (exempt -> salary, non-exempt -> hourly). It's the user's own
  // attribute, so no manual toggle is needed; the Salary/Hourly pill only
  // appears as a fallback when the record can't determine a lane (no employee
  // resolved / payClass + exempt both unset). `effectivePayType` derives the
  // lane without mutating the store.
  const payClass = tsEmployee?.payClass
  const boundPayLane: 'salary' | 'hourly' | null =
    payClass === 'hourly' || payClass === 'salary'
      ? payClass
      : tsEmployee
        ? (tsEmployee.exempt ? 'salary' : 'hourly')
        : null
  // Always the persona's own lane; default to salary only in the rare case the
  // employee record didn't resolve (no manual toggle - pay class is intrinsic).
  const effectivePayType = boundPayLane ?? 'salary'
  // Whether the current user should see the first-class Approval Queue: an RBAC
  // approver role (real auth), OR - for the demo, where persona-switching is how
  // you change "who you are" - a persona who has direct reports (someone whose
  // supervisor is this persona). ICs see no Approval Queue.
  const isTimeApprover =
    canApproveTimeRole(accessSubject?.systemRole) ||
    (!!tsEmployee && timesheetDataService.getEmployees().some((e) => e.supervisorId === tsEmployee.id))
  // First adoption of the feature flag system (item 3 of customer rollout
  // build plan). Onboarding link is hidden until ops flips this on per-org
  // or per-environment. See Playbooks/Feature Flag Convention.md.
  const showOnboardingLink = useFlag<boolean>('onboarding.guided_checklist') ?? false

  // Agile Delivery nav group: visible only when the agile.enabled capability is
  // on AND the selected project is Agile or Hybrid. The route literals stay in
  // source (agileNavGroup) so the coverage gates still map them; this only gates
  // runtime visibility.
  const agileEnabled = useFlag<boolean>('agile.enabled') ?? false
  // Off (default) keeps the Startup Checklist on construction / E&C projects
  // only; on offers it for every project type.
  const startupChecklistAllTypes = useFlag<boolean>('startupChecklist.allProjectTypes') ?? false
  const projectMethodology =
    (selectedProject as { deliveryMethodology?: string } | null)?.deliveryMethodology ??
    ((selectedProject as { schedulingMethod?: string } | null)?.schedulingMethod === 'agile' ? 'agile' : 'waterfall')
  const showAgileNav = agileEnabled && (projectMethodology === 'agile' || projectMethodology === 'hybrid')
  const effectiveProjectGroups: NavGroup[] = showAgileNav
    ? [...projectNavGroups, agileNavGroup]
    : projectNavGroups

  // Training shows when it is on for this person's organization. Setup >
  // Training stays visible to administrators so they can turn it on.
  const trainingOn = useFlag<boolean>('training.enabled') === true
  // Landing pages: Home when an administrator gave this person a home page,
  // and the published pages an administrator put in the sidebar. Read only
  // inside Workflow and Forms.
  const inWorkflowMgmt = managementContext === 'workflow-management'
  const homeQ = useQuery({ queryKey: ['landing-home'], queryFn: getLandingHome, retry: false, staleTime: 60_000, enabled: inWorkflowMgmt })
  const pagesQ = useQuery({ queryKey: ['landing-pages'], queryFn: listLandingPages, retry: false, staleTime: 60_000, enabled: inWorkflowMgmt })
  const navPages = (pagesQ.data ?? [])
    .filter((p) => p.showInNav && p.publishedAt)
    .sort((a, b) => a.navOrder - b.navOrder || a.name.localeCompare(b.name))
  const pagesGroup: NavGroup | null = navPages.length
    ? { label: 'Pages', items: navPages.map((p) => ({ label: p.name, icon: <LandingIcon name={p.icon} fallback="home" size={16} />, path: pagePath(p.slug), key: `lp-${p.id}` })) }
    : null
  const effectiveWorkflowMgmtGroups: NavGroup[] = workflowMgmtNavGroups.flatMap((g, i) => {
    if (i === 0) {
      const first = homeQ.data ? { ...g, items: [{ label: 'Home', icon: <Home size={16} />, path: '/workflow-management/home', key: 'wm-landing-home' }, ...g.items] } : g
      return pagesGroup ? [first, pagesGroup] : [first]
    }
    const group = g.label === 'Setup' && (trainingOn || canAccessConfig) ? { ...g, items: [...g.items, trainingSetupItem] } : g
    return g.label === 'Operate' && trainingOn ? [group, trainingNavGroup] : [group]
  })

  function handleSwitchContext() {
    setMobileNavOpen(false)
    useAppStore.getState().selectPortfolio(null)
    useAppStore.getState().selectProject(null)
    useAppStore.getState().setManagementContext(null)
    navigate('/')
  }

  // Navigate AND close the mobile drawer. Direct-navigate buttons (the
  // bottom-anchored Admin & Reporting / Settings rows) must use this - calling
  // navigate() alone leaves the off-canvas drawer covering the page on mobile,
  // so the tap looks like it did nothing. Leaf nav items go through
  // handleNavClick, which already closes the drawer.
  function navAndClose(path: string) {
    setMobileNavOpen(false)
    navigate(path)
  }

  function handleNavClick(item: NavItem) {
    // Tapping any nav item closes the mobile drawer (no-op on desktop).
    setMobileNavOpen(false)
    if (item.key === 'config-enter') {
      useAppStore.getState().setManagementContext('configuration')
      navigate('/config')
      return
    }
    // If the user clicks a project-only item (e.g. Forecast Management,
    // Scheduling, EVM) we make sure the sidebar context matches the page
    // they're heading to. Without this, jumping in from a non-project
    // context - Command Palette, refresh, or a stale session - leaves
    // the sidebar showing portfolio nav while a project-level page renders.
    if (item.projectOnly && managementContext !== 'project') {
      useAppStore.getState().setManagementContext('project')
    }
    navigate(item.path)
  }

  function isActive(path: string): boolean {
    // A deep link into a tab (path?param=value) is active only on that tab,
    // and then the page's own plain entry is not, so one item lights up.
    const q = path.indexOf('?')
    if (q >= 0) {
      if (location.pathname !== path.slice(0, q)) return false
      const have = new URLSearchParams(location.search)
      return [...new URLSearchParams(path.slice(q + 1))].every(([k, v]) => have.get(k) === v)
    }
    if (path === WM_STUDIO_PATH && location.pathname === path) {
      const tab = new URLSearchParams(location.search).get('wf3Tab')
      if (tab === 'audit' || tab === 'policies') return false
    }
    // My Training is the parent path of the other training pages: exact only.
    if (path === trainingNavGroup.items[0]!.path) return location.pathname === path
    // The request register lives under Reports but has its own entry.
    if (path === '/workflow-management/reports' && location.pathname.startsWith('/workflow-management/reports/request-register')) return false
    // Landing Pages lights on the list and the editor; a page's own address lights its Pages entry.
    if (path === '/workflow-management/pages') return location.pathname === path || location.pathname.endsWith('/edit')
    return location.pathname === path || location.pathname.startsWith(path + '/')
  }

  const contextLabel = managementContext
    ? { portfolio: 'Portfolio', project: 'Project', contract: 'Contract', organizational: 'Organization', supplychain: 'Supply Chain', configuration: 'Configuration', timesheet: 'Timesheet', mywork: 'My Work', personnel: 'Personnel', 'workflow-management': 'Workflow and Forms Management', cost: 'Cost Management', crm: 'Customer Relationship Management', analytics: 'Analytics', 'it-ticketing': 'IT Ticketing' }[managementContext]
    : 'Portfolio'

  // Uniform context-header identity — every context resolves to the SAME
  // { title, subtitle } shape so the header renders through one path and can't
  // drift in form. title = what the context is scoped to (project / portfolio /
  // person / org); subtitle = a short descriptor. Replaces the old ad-hoc paths
  // that left Organization/Personnel blank and leaked the last-selected
  // portfolio into Supply Chain / My Work / Workflow.
  const orgName = currentUser.organizationName || 'Organization'
  const contextIdentity: { title: string; subtitle: string } = (() => {
    switch (managementContext) {
      case 'project':
        return selectedProject
          ? { title: selectedProject.name, subtitle: selectedProject.code || 'Project' }
          : { title: 'Select a project', subtitle: 'Back to home' }
      case 'portfolio':
        return portfolio
          ? { title: portfolio.name, subtitle: portfolio.level > 0 ? `Level ${portfolio.level}` : 'Portfolio' }
          : { title: 'Select a portfolio', subtitle: 'Back to home' }
      case 'contract':
        return { title: portfolio?.name || 'Contracts', subtitle: 'Contract management' }
      case 'timesheet':
        return { title: orgName, subtitle: 'Org-wide timesheet' }
      case 'organizational':
        return { title: orgName, subtitle: 'Organization structure' }
      case 'supplychain':
        return { title: orgName, subtitle: 'Supply chain' }
      case 'configuration':
        return { title: orgName, subtitle: 'Configuration & policies' }
      case 'mywork':
        return { title: currentUser.name || orgName, subtitle: 'My work' }
      case 'personnel':
        return { title: orgName, subtitle: 'Personnel directory' }
      case 'workflow-management':
        return { title: orgName, subtitle: 'Workflows & forms' }
      case 'cost':
        return { title: orgName, subtitle: 'Cost accounting' }
      case 'crm':
        return { title: orgName, subtitle: 'Customer relationships' }
      case 'analytics':
        return { title: orgName, subtitle: 'Conversational analytics' }
      default:
        return { title: orgName, subtitle: '' }
    }
  })()

  // Choose nav groups based on context. Portfolio / Project / Config are
  // sectioned by lifecycle phase (Plan / Execute / Monitor / etc.); shorter
  // contexts stay flat as a single group. Timesheet has its own assembly
  // path (pill switcher + collapsible admin) and skips this pipeline.
  // Filter out feature-flag-gated items. Today there's just one gated
  // nav item (Onboarding); pattern can extend as more flags get adopted.
  const filteredPortfolioGroups: NavGroup[] = showOnboardingLink
    ? portfolioNavGroups
    : portfolioNavGroups.map((g) => ({
        ...g,
        items: g.items.filter((i) => i.key !== 'portfolio-onboarding'),
      }))

  const primaryGroups: NavGroup[] = isMyWorkContext
    ? [{ items: myWorkNavItems }]
    : isConfigContext
    ? configNavGroups
    : isContractContext
    ? [{ items: contractNavItems }]
    : isSupplyChainContext
    ? supplyChainNavGroups
    : isPersonnelContext
    ? [{ items: personnelNavItems }]
    : isOrganizationalContext
    ? [{ items: organizationalNavItems }]
    : isWorkflowMgmtContext
    ? effectiveWorkflowMgmtGroups
    : isCostContext
    ? [{ items: costNavItems }]
    : isCrmContext
    ? [{ items: crmNavItems }]
    : isItTicketingContext
    ? [{ items: isTicketingAdmin ? itTicketingNavItems : itTicketingNavItems.filter((i) => i.key !== itTicketingAdminNavKey) }]
    : isAnalyticsContext
    ? [{ items: analyticsNavItems }]
    : isProjectContext
    ? effectiveProjectGroups
    : filteredPortfolioGroups

  // Relabel "Baseline Management" → "Budget Management" for E&C projects.
  // Centralized in projectClassification.ts so the same rule fires across
  // sidebar, breadcrumb, command palette, and the page header.
  const financeLabel = getFinancePageLabel(
    (selectedProject as { projectType?: string | null } | null)?.projectType ?? null
  )
  const relabeled = financeLabel === 'Baseline Management'
    ? primaryGroups
    : primaryGroups.map((g) => ({
        ...g,
        items: g.items.map((it) => it.key === 'finance' ? { ...it, label: financeLabel } : it),
      }))
  // Filter conditional items: the Startup Checklist is a construction / E&C
  // startup gate, so it shows only on those projects while they are in Startup
  // status. Flipping startupChecklist.allProjectTypes in Configuration >
  // Capabilities offers it on every project type. Drop empty groups.
  const showStartupChecklist = isStartupChecklistApplicable(
    selectedProject as { status?: string | null; projectType?: string | null } | null,
    startupChecklistAllTypes,
  )
  const navGroups = relabeled
    .map((g) => ({
      ...g,
      items: g.items.filter(
        (it) =>
          (!it.requiresStartupStatus || showStartupChecklist) &&
          // Never offer a nav entry whose route this deployment did not mount,
          // or that this user's module entitlement would bounce.
          (!it.module || isModuleVisible(it.module, entitlement)) &&
          isPathAllowed(it.path, entitlement) &&
          // Nor one whose catalogued feature this deployment did not build.
          (!it.feature || isFeatureBuilt(it.feature)),
      ),
    }))
    .filter((g) => g.items.length > 0)

  return (
    <Tooltip.Provider delayDuration={200}>
    {/* Mobile drawer backdrop - tap to close. */}
    {isMobile && mobileNavOpen && (
      <div
        className="fixed inset-0 z-40 bg-black/50 animate-fade-in"
        onClick={() => setMobileNavOpen(false)}
        aria-hidden="true"
      />
    )}
    <aside
      className={cn(
        'fixed left-0 top-0 h-full text-white flex flex-col pb-safe-bottom',
        'transition-all duration-300',
        // Desktop: fixed rail/expanded width. Mobile: full-width slide-in drawer.
        isMobile ? 'w-64 z-50' : 'z-30',
        !isMobile && (expanded ? 'w-52' : 'w-14'),
        isMobile && !mobileNavOpen ? '-translate-x-full' : 'translate-x-0',
        isMyWorkContext ? 'bg-gradient-to-b from-blue-900 via-cyan-900 to-slate-900' : isConfigContext ? 'bg-zinc-900' : isTimesheetContext ? 'bg-brand-800' : isContractContext ? 'bg-brand-800' : isSupplyChainContext ? 'bg-teal-900' : isPersonnelContext ? 'bg-rose-900' : isOrganizationalContext ? 'bg-amber-900' : isWorkflowMgmtContext ? 'bg-indigo-900' : isProjectContext ? 'bg-slate-900' : 'bg-brand-800'
      )}
      role="navigation"
      aria-label="Main navigation"
    >
      {/* Logo area */}
      <div className="flex items-center h-12 px-3 border-b border-white/10">
        <div className="flex items-center gap-2 min-w-0">
          <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
            <defs><linearGradient id="sidebar-g" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#2563EB"/><stop offset="100%" stopColor="#06B6D4"/></linearGradient></defs>
            <g transform="translate(16, 16)">
              <rect x="-12" y="-12" width="24" height="24" fill="none" stroke="url(#sidebar-g)" strokeWidth="1.2" opacity="0.28"/>
              <rect x="-5.5" y="-5.5" width="11" height="11" fill="none" stroke="url(#sidebar-g)" strokeWidth="1.2" opacity="0.85"/>
              <line x1="-12" y1="-12" x2="-5.5" y2="-5.5" stroke="url(#sidebar-g)" strokeWidth="0.7" opacity="0.35"/>
              <line x1="12" y1="-12" x2="5.5" y2="-5.5" stroke="url(#sidebar-g)" strokeWidth="0.7" opacity="0.35"/>
              <line x1="12" y1="12" x2="5.5" y2="5.5" stroke="url(#sidebar-g)" strokeWidth="0.7" opacity="0.35"/>
              <line x1="-12" y1="12" x2="-5.5" y2="5.5" stroke="url(#sidebar-g)" strokeWidth="0.7" opacity="0.35"/>
              <polygon points="-12,-12 12,-12 5.5,-5.5 -5.5,-5.5" fill="#2563EB" opacity="0.2"/>
              <polygon points="12,-12 12,12 5.5,5.5 5.5,-5.5" fill="#3B82F6" opacity="0.15"/>
              <polygon points="-12,12 12,12 5.5,5.5 -5.5,5.5" fill="#06B6D4" opacity="0.11"/>
              <polygon points="-12,-12 -12,12 -5.5,5.5 -5.5,-5.5" fill="#1D4ED8" opacity="0.17"/>
              <rect x="-2" y="-2" width="4" height="4" rx="0.7" fill="url(#sidebar-g)" opacity="0.9"/>
            </g>
          </svg>
          {expanded && (
            <div className="flex flex-col animate-fade-in min-w-0">
              <span className="font-display font-bold text-[11px] tracking-wide truncate">TESSERACT <span className="text-cyan-400">XPM</span></span>
            </div>
          )}
        </div>
      </div>

      {/* Context indicator — ONE uniform header for every context (title +
          subtitle from contextIdentity, above). Click switches context / home. */}
      {expanded && managementContext && (
        <button
          onClick={handleSwitchContext}
          className="w-full text-left px-3 py-2 border-b border-white/10 hover:bg-white/5 transition-colors group"
          aria-label="Back to home - switch context"
          title="Back to home"
        >
          <div className="flex items-center justify-between gap-2 mb-1">
            <span className="text-[10px] uppercase tracking-wider text-white/50">{contextLabel} Context</span>
            <ArrowLeftRight size={12} className="text-white/30 group-hover:text-white/70 transition-colors" />
          </div>
          <div className="text-body-sm font-medium truncate">{contextIdentity.title}</div>
          {contextIdentity.subtitle && (
            <div className="text-[10px] mt-0.5 uppercase tracking-wider text-white/50 truncate">
              {contextIdentity.subtitle}
            </div>
          )}
        </button>
      )}
      {/* Collapsed context indicator - small icon button. Always shown so every
          context has a way back home, even with no portfolio/project selected. */}
      {!expanded && (
        <SidebarTooltip label="Back to home" enabled={true}>
          <button
            onClick={handleSwitchContext}
            className="w-full flex items-center justify-center py-3 border-b border-white/10 text-white/50 hover:text-white hover:bg-white/5 transition-colors"
            aria-label="Switch context"
          >
            <ArrowLeftRight size={16} />
          </button>
        </SidebarTooltip>
      )}

      {/* Scrollable nav area - hide scrollbar for clean look */}
      <nav className="flex-1 py-1 overflow-y-auto scrollbar-hide" aria-label={isTimesheetContext ? 'Timesheet navigation' : isContractContext ? 'Contract navigation' : isProjectContext ? 'Project navigation' : 'Portfolio navigation'}>
        {isTimesheetContext ? (
          // Timesheet sidebar = Dashboard + pay-type pill + active worker
          // items only. Admin / compliance / audit lives behind a single
          // bottom-anchored button (rendered below, outside the scroll
          // area) that navigates to /timesheet/admin.
          <>
            <NavSection
              items={[timesheetDashboardItem]}
              expanded={expanded}
              isActive={isActive}
              onNav={handleNavClick}
            />

            {/* My Shortcuts - the admin/reporting surfaces this persona pinned in
                the hub (★). Mirrors the hub + dashboard via the shared pins store.
                Hidden when nothing is pinned. */}
            {shortcutNavItems.length > 0 && (
              <NavSection
                items={shortcutNavItems}
                label="My Shortcuts"
                expanded={expanded}
                isActive={isActive}
                onNav={handleNavClick}
              />
            )}

            {isSubcontractor ? (
              /* Subcontractor nav - self-entry + (lead only) approvals.
                 No pay-type pill: subs charge against the prime's codes. */
              <>
                {expanded && (
                  <div className="px-4 pt-2 pb-0.5 text-[9px] uppercase tracking-wider text-white/35 font-medium">
                    {isSubLeadPersona ? 'Subcontractor · Lead' : 'Subcontractor'}
                  </div>
                )}
                <NavSection
                  items={isSubLeadPersona ? timesheetSubLeadItems : timesheetSubcontractorItems}
                  expanded={expanded}
                  isActive={isActive}
                  onNav={handleNavClick}
                />
              </>
            ) : (
              <>
                {/* Worker items for the pay lane, role-gated (#1 P2), now split into
                    labeled "My Time" / "Team" sections so the nav is scannable. The
                    lane is always the persona's own (boundPayLane); no manual toggle. */}
                {buildWorkerNavItems(
                  effectivePayType,
                  tsEmployee?.workCategory,
                  accessSubject?.systemRole,
                  isTimeApprover,
                )
                  // Drop anything already pinned under My Shortcuts (no duplication).
                  .map((group) => ({
                    ...group,
                    items: group.items.filter((i) => !pinnedShortcutPaths.has(i.path)),
                  }))
                  .filter((group) => group.items.length > 0)
                  .map((group, idx) => (
                    <div key={group.label ?? `ts-group-${idx}`}>
                      {idx > 0 && expanded && <div className="ml-4 mt-1.5 mb-0 w-1/3 border-t border-white/10" />}
                      {idx > 0 && !expanded && <div className="my-0.5" />}
                      <NavSection
                        items={group.items}
                        label={group.label}
                        expanded={expanded}
                        isActive={isActive}
                        onNav={handleNavClick}
                      />
                    </div>
                  ))}
              </>
            )}
          </>
        ) : (
          <>
            {navGroups.map((group, idx) => (
              <div key={group.label ?? `group-${idx}`}>
                {idx > 0 && expanded && <div className="ml-4 mt-1.5 mb-0 w-1/3 border-t border-white/10" />}
                {idx > 0 && !expanded && <div className="my-0.5" />}
                <NavSection
                  items={group.items}
                  label={group.label}
                  expanded={expanded}
                  isActive={isActive}
                  onNav={handleNavClick}
                />
              </div>
            ))}

            {/* Show tools section only in portfolio context */}
            {!isProjectContext && !isContractContext && !isConfigContext && !isWorkflowMgmtContext && (
              <>
                {expanded && <div className="ml-4 mt-1.5 mb-0 w-1/3 border-t border-white/10" />}
                {!expanded && <div className="my-0.5" />}

                <NavSection
                  items={toolNavItems.filter(item => !item.projectOnly)}
                  label="Tools"
                  expanded={expanded}
                  isActive={isActive}
                  onNav={handleNavClick}
                />
              </>
            )}
          </>
        )}
      </nav>

      {/* Admin & Reporting + Settings - bottom-anchored entries, timesheet
          context only. Settings is split out as its own row so demo
          editing + walkthroughs (persona switching, schedule flipping,
          grace-period tweaks) is one click instead of two. */}
      {isTimesheetContext && (
        <div className="border-t border-white/10 py-1 px-2 space-y-0.5">
          <SidebarTooltip label="Admin & Reporting" enabled={!expanded}>
            <button
              type="button"
              onClick={() => navAndClose('/timesheet/admin')}
              className={cn(
                'w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-[12px] transition-colors duration-150',
                isActive('/timesheet/admin')
                  ? 'bg-brand-500 text-white'
                  : 'text-white/70 hover:bg-white/10 hover:text-white'
              )}
              aria-current={isActive('/timesheet/admin') ? 'page' : undefined}
              aria-label={!expanded ? 'Admin & Reporting' : undefined}
            >
              <span className="flex-shrink-0"><Settings2 size={16} /></span>
              {expanded && <span className="truncate">Admin & Reporting</span>}
            </button>
          </SidebarTooltip>
          <SidebarTooltip label="Settings" enabled={!expanded}>
            <button
              type="button"
              onClick={() => navAndClose('/timesheet/settings')}
              className={cn(
                'w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-[12px] transition-colors duration-150',
                isActive('/timesheet/settings')
                  ? 'bg-brand-500 text-white'
                  : 'text-white/70 hover:bg-white/10 hover:text-white'
              )}
              aria-current={isActive('/timesheet/settings') ? 'page' : undefined}
              aria-label={!expanded ? 'Settings' : undefined}
            >
              <span className="flex-shrink-0"><Settings size={16} /></span>
              {expanded && <span className="truncate">Settings</span>}
            </button>
          </SidebarTooltip>
        </div>
      )}

      {/* Configuration context entry point - only for authorized users */}
      {canAccessConfig && !isConfigContext && (
        <div className="border-t border-white/10 py-1 px-2">
          <SidebarTooltip label="Configuration" enabled={!expanded}>
            <button
              onClick={() => handleNavClick({ label: 'Configuration', icon: <Settings size={16} />, path: '/config/users', key: 'config-enter' })}
              className="w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-[12px] text-white/70 hover:bg-white/10 hover:text-white transition-colors duration-150"
              aria-label={!expanded ? 'Configuration' : undefined}
            >
              <span className="flex-shrink-0"><Settings size={16} /></span>
              {expanded && <span className="truncate">Configuration</span>}
            </button>
          </SidebarTooltip>
        </div>
      )}

      {/* Collapse toggle + version */}
      <div className="border-t border-white/10 flex items-center h-10">
        <button
          onClick={toggleSidebar}
          className="flex items-center justify-center flex-1 h-full hover:bg-white/10 transition-colors"
          aria-label={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
        >
          {expanded ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
        </button>
        {expanded && (
          <span className="pr-3 text-[10px] text-white/25 select-none tabular-nums" title={APP_VERSION_FULL}>
            v{APP_VERSION_FULL}
          </span>
        )}
      </div>
    </aside>
    </Tooltip.Provider>
  )
}
